import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowRight, Check } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button, BotonEnlace } from '@/components/Button';
import { EstadoVacio, Seccion } from '@/components/Card';
import { db } from '@/db/schema';
import { aplicarDescansosConfirmados, leerCambiosDeDescanso, type CambioDeDescanso } from '@/db/rutinas';
import { mensajeDeError } from '@/db/tx';
import { textoDescanso } from '@/domain/entrenamiento';
import { textoDescansosConfirmados } from './Precargadas';

/**
 * Descansos confirmados (26 oct 2026): qué cambia en las rutinas del
 * gimnasio que salieron de una precargada, para revisarlo antes de
 * aplicarlo. Solo cambian los descansos.
 */
export function DescansosPage() {
  const cambios = useLiveQuery(() => leerCambiosDeDescanso(), []);
  const ejercicios = useLiveQuery(() => db.exercises.toArray(), []);
  const [guardando, setGuardando] = useState(false);
  const [hechos, setHechos] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (cambios === undefined || ejercicios === undefined) return null;

  const volver = { a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' };
  const nombres = new Map(ejercicios.map((x) => [x.id, x.nombre]));
  const total = cambios.reduce((suma, r) => suma + r.cambios.length, 0);

  async function aplicar() {
    if (guardando) return;
    setGuardando(true);
    setError(null);
    try {
      setHechos(await aplicarDescansosConfirmados());
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  if (cambios.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader antetitulo="Gimnasio" titulo="Descansos confirmados." volver={volver} />
        {hechos !== null && (
          <Banner tono="exito" titulo="Descansos actualizados">
            {hechos === 1 ? 'Cambió 1 descanso.' : `Cambiaron ${hechos} descansos.`} Las cargas, las series y tu historial
            siguen igual.
          </Banner>
        )}
        <EstadoVacio titulo="Tus rutinas ya tienen los descansos confirmados." descripcion="No hay nada que cambiar.">
          <BotonEnlace to="/gym" variante="secundario" icono={ArrowRight}>
            Ir a Gym
          </BotonEnlace>
        </EstadoVacio>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo="Gimnasio"
        titulo="Descansos confirmados."
        descripcion={textoDescansosConfirmados(cambios)}
        volver={volver}
      />

      <Banner tono="info" titulo="Solo cambian los descansos">
        En tus rutinas precargadas y sus copias. Las cargas, las series, tus entrenamientos y tus otras rutinas no cambian.
        Si tienes un entrenamiento abierto, conserva los suyos.
      </Banner>

      {cambios.map((r) => (
        <Seccion key={r.routineId} titulo={r.nombre} extra={r.cambios.length === 1 ? '1 cambio' : `${r.cambios.length} cambios`}>
          <ul className="divide-y divide-borde">
            {r.cambios.map((c) => (
              <li key={`${c.itemId}-${c.campo}`} className="flex items-baseline justify-between gap-3 py-3">
                <span className="min-w-0">
                  <span className="block text-[14px] font-medium text-texto">{nombres.get(c.exerciseId) ?? 'Ejercicio'}</span>
                  <span className="block text-[12px] text-texto-suave">{textoCampo(c)}</span>
                </span>
                <span className="whitespace-nowrap text-[13px] tabular-nums text-texto">
                  {c.antes === null ? 'Sin dato' : textoDescanso(c.antes)} → {textoDescanso(c.despues)}
                </span>
              </li>
            ))}
          </ul>
        </Seccion>
      ))}

      {error !== null && (
        <Banner tono="alerta" titulo="No se actualizaron">
          {error}
        </Banner>
      )}
      <Button icono={Check} tamano="grande" disabled={guardando} onClick={() => void aplicar()}>
        {guardando ? 'Actualizando…' : total === 1 ? 'Actualizar 1 descanso' : `Actualizar ${total} descansos`}
      </Button>
    </div>
  );
}

function textoCampo(c: CambioDeDescanso): string {
  return c.campo === 'descansoBiserieSeg' ? 'Biserie: al terminar los dos' : 'Descanso';
}
